import { Button } from '@ipfabric/ui-components/Button/Button';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { isReservedLabelName } from '@ipfabric/utils/labels';
import { useQueryClient } from '@tanstack/react-query';
import type {
  TableActionsItem,
  TableBulkAction,
  TableBulkActionClickEvent,
} from 'components/Table/model/TableModel';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { useMemo, useState } from 'react';
import type { EditableLabel } from './labelCatalogApi';
import { getGetLabelsQueryKey } from './labelCatalogApi';
import { createLabelsTableColumns } from './labelsTableColumns';
import { useLabelDeleteActions } from './useLabelDeleteActions';
import { useLabelsQuery } from './useLabelsQuery';

export const useLabelsTable = () => {
  const queryClient = useQueryClient();
  const confirmDeleteDialog = useSimpleConfirmDialog();
  const [editedLabel, setEditedLabel] = useState<EditableLabel | 'new' | null>(
    null,
  );

  const labelsQuery = useLabelsQuery();

  const invalidateLabels = useStableCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: getGetLabelsQueryKey() });
  });

  const { requestDeleteLabels } = useLabelDeleteActions({
    confirmDeleteDialog,
    invalidateLabels,
  });

  const columns = useMemo(
    () =>
      createLabelsTableColumns({
        onEdit: setEditedLabel,
        onDelete: (id, name) => {
          void requestDeleteLabels([id], name);
        },
      }),
    [requestDeleteLabels],
  );

  const frontendSimpleFilter = useFrontendLocalTable({
    columns,
    data: labelsQuery.data ?? [],
  });

  const bulkActions: TableBulkAction[] = [
    {
      key: 'delete',
      title: 'Delete',
      accent: 'negative',
      disableAutoDeselect: true,
      icon: 'fa-trash-can',
      onClick: async ({
        selectedRows,
        deselectAll,
      }: TableBulkActionClickEvent) => {
        const selectedIds = new Set([...selectedRows].map(String));
        const ids = (labelsQuery.data ?? [])
          .filter(
            (label) =>
              selectedIds.has(label.id) && !isReservedLabelName(label.name),
          )
          .map((label) => label.id);
        if (ids.length === 0) {
          return;
        }
        if (await requestDeleteLabels(ids)) {
          deselectAll();
        }
      },
    },
  ];

  const menuItems: TableActionsItem[] = [
    {
      standalone: true,
      elementWithKey: (
        <Button
          dataTestId='labels-add-new'
          key='addLabel'
          icon='fa-plus-circle'
          onClick={() => setEditedLabel('new')}
          size='small'
          type='button'
          variant='outline'
        >
          Add new
        </Button>
      ),
    },
  ];

  return {
    bulkActions,
    columns,
    confirmDeleteDialog,
    editedLabel,
    frontendSimpleFilter,
    invalidateLabels,
    labelsQuery,
    menuItems,
    setEditedLabel,
  };
};
